/**
 * Rolling speed graph: planar speed (green on ground, violet in air) and
 * vertical speed (cyan) over the last ten seconds, with movement events
 * marked as labelled ticks. One sample per simulation tick.
 */
import type { MovementEvent, MovementEventKind } from './MovementEvents';

export interface SpeedSample {
  tick: number;
  planar: number;
  vertical: number;
  grounded: boolean;
}

const EVENT_STYLE: Record<MovementEventKind, { color: string; label: string }> = {
  jump: { color: '#b48cff', label: 'J' },
  release: { color: '#ff9cf0', label: 'C' },
  land: { color: '#7dffb0', label: 'L' },
  launch: { color: '#ffe27a', label: '^' },
  impact: { color: '#ff9a4d', label: '!' },
  death: { color: '#ff4040', label: 'x' },
  respawn: { color: '#9aa4b8', label: 'R' },
};

export class SpeedGraph {
  private readonly samples: SpeedSample[];
  private head = 0;
  private count = 0;

  constructor(readonly capacity = 1200) {
    this.samples = Array.from({ length: capacity }, () => ({ tick: 0, planar: 0, vertical: 0, grounded: false }));
  }

  push(tick: number, planar: number, vertical: number, grounded: boolean): void {
    const s = this.samples[this.head]!;
    s.tick = tick;
    s.planar = planar;
    s.vertical = vertical;
    s.grounded = grounded;
    this.head = (this.head + 1) % this.capacity;
    this.count = Math.min(this.count + 1, this.capacity);
  }

  clear(): void {
    this.count = 0;
    this.head = 0;
  }

  /** Samples oldest → newest. */
  ordered(): SpeedSample[] {
    const out: SpeedSample[] = [];
    for (let i = 0; i < this.count; i++) out.push(this.samples[(this.head - this.count + i + this.capacity) % this.capacity]!);
    return out;
  }

  peak(): number {
    let m = 0;
    for (const s of this.ordered()) m = Math.max(m, s.planar);
    return m;
  }

  draw(canvas: HTMLCanvasElement, events: readonly MovementEvent[]): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    const data = this.ordered();
    if (data.length < 2) return;
    let max = 12;
    for (const s of data) max = Math.max(max, s.planar * 1.1, Math.abs(s.vertical) * 1.1);
    const zeroY = h * 0.7;
    const scale = (h * 0.68) / max;
    const firstTick = data[0]!.tick;
    const x = (tick: number): number => ((tick - firstTick) / (this.capacity - 1)) * w;

    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, zeroY);
    ctx.lineTo(w, zeroY);
    for (let v = 10; v < max; v += 10) {
      ctx.moveTo(0, zeroY - v * scale);
      ctx.lineTo(w, zeroY - v * scale);
    }
    ctx.stroke();

    ctx.lineWidth = 1;
    ctx.strokeStyle = '#4fd8ff';
    ctx.beginPath();
    data.forEach((s, i) => (i === 0 ? ctx.moveTo(x(s.tick), zeroY - s.vertical * scale) : ctx.lineTo(x(s.tick), zeroY - s.vertical * scale)));
    ctx.stroke();

    ctx.lineWidth = 2;
    for (let i = 1; i < data.length; i++) {
      const a = data[i - 1]!;
      const b = data[i]!;
      ctx.strokeStyle = b.grounded ? '#7dffb0' : '#b48cff';
      ctx.beginPath();
      ctx.moveTo(x(a.tick), zeroY - a.planar * scale);
      ctx.lineTo(x(b.tick), zeroY - b.planar * scale);
      ctx.stroke();
    }

    ctx.font = '10px ui-monospace, monospace';
    for (const e of events) {
      if (e.tick < firstTick) continue;
      const st = EVENT_STYLE[e.kind];
      const ex = x(e.tick);
      ctx.strokeStyle = st.color;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(ex, 0);
      ctx.lineTo(ex, h);
      ctx.stroke();
      ctx.fillStyle = st.color;
      ctx.fillText(st.label, ex + 2, 10);
    }
    ctx.fillStyle = 'rgba(230,230,240,0.8)';
    ctx.fillText(`${max.toFixed(0)} m/s`, 2, h * 0.7 - max * scale + 10);
  }
}
